import { useEffect, useRef, useState } from "react";
import { useRealtime } from "../utils/RealtimeProvider";
import { t } from "i18next";
import { Connected as ConnectedIcon } from "../../../components/icons/Connected";

export const RealtimeHeader = () => {
  const { isConnected, realtimeData, toggleConnection } = useRealtime();
  const [showStillHere, setShowStillHere] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const inactivityTimerRef = useRef<number | null>(null);
  const autoDisconnectTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (realtimeData) {
      setLastUpdated(new Date());
    }
  }, [realtimeData]);

  useEffect(() => {
    const INACTIVITY_MS = 10 * 60 * 1000;
    const GRACE_MS = 60 * 1000;

    const clearTimers = () => {
      if (inactivityTimerRef.current) window.clearTimeout(inactivityTimerRef.current);
      if (autoDisconnectTimerRef.current) window.clearTimeout(autoDisconnectTimerRef.current);
      inactivityTimerRef.current = null;
      autoDisconnectTimerRef.current = null;
    };

    const startInactivityTimer = () => {
      clearTimers();
      if (!isConnected) return;
      inactivityTimerRef.current = window.setTimeout(() => {
        setShowStillHere(true);
        autoDisconnectTimerRef.current = window.setTimeout(() => {
          setShowStillHere(false);
          if (isConnected) toggleConnection();
        }, GRACE_MS);
      }, INACTIVITY_MS);
    };

    const resetActivity = () => {
      if (!isConnected) return;
      if (showStillHere) return;
      startInactivityTimer();
    };

    if (isConnected) {
      startInactivityTimer();
      window.addEventListener("mousemove", resetActivity);
      window.addEventListener("keydown", resetActivity);
      window.addEventListener("click", resetActivity);
    }

    return () => {
      window.removeEventListener("mousemove", resetActivity);
      window.removeEventListener("keydown", resetActivity);
      window.removeEventListener("click", resetActivity);
      clearTimers();
    };
  }, [isConnected, toggleConnection, showStillHere]);

  return (
    <div className="">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-3xl" style={{ color: "#1C67AD" }}>
          {t("realtimeOverview.title")}
        </h4>

        <div className="flex items-center gap-4">
          {!isConnected ? (
            <button
              onClick={toggleConnection}
              className="inline-flex items-center gap-2 px-4 py-2 text-white"
              style={{ backgroundColor: "#1C67AD" }}
            >
              <ConnectedIcon className="text-white" width={16} height={16} />
              {t("realtimeOverview.connect")}
            </button>
          ) : (
            <div className="flex items-center gap-4">
              <div
                className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm"
                style={{ backgroundColor: "#87C38E", color: "white" }}
              >
                <ConnectedIcon className="text-white" width={16} height={16} />
                {t("realtimeOverview.connected")}
              </div>
              <button onClick={toggleConnection} className="text-sm underline" style={{ color: "#1C67AD" }}>
                {t("realtimeOverview.disconnect")}
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="" style={{ color: "#606060" }}>
        <div>{t("realtimeOverview.dataRefreshesAutomaticallyEvery3Minutes")}</div>
        <div>{t("realtimeOverview.lastUpdated")}: {lastUpdated.toLocaleTimeString()} {Intl.DateTimeFormat().resolvedOptions().timeZone}</div>
      </div>

      {showStillHere && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="rounded-lg bg-white p-6 shadow-lg">
            <p className="mb-4">{t("reports.areYouStillHere")}</p>
            <button
              onClick={() => {
                setShowStillHere(false);
              }}
              className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              {t("reports.yesImHere")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
